/**
 * @file     useAgentMode.ts
 * @purpose  Detects "agent submitting a lead on behalf of a prospect" — i.e. an admin is signed in and the NEXT_PUBLIC_AGENT_MODE_ENABLED feature flag is on. In that mode the lead-capture flows must skip autoSignIn so the agent's session is never replaced by the prospect's, and the lead is attributed back to the agent server-side.
 */
'use client';

import { useSupabaseUser } from './useSupabaseUser';
import { useIsAdmin } from './useIsAdmin';

export type AgentModeState = {
  /** True when an admin is signed in and the agent-mode env flag is enabled. */
  active: boolean;
  /** The signed-in agent's user id, when active. */
  agentUserId: string | null;
  /** True while either user identity or admin flag is still resolving. */
  loading: boolean;
};

export function useAgentMode(): AgentModeState {
  const { user, loading: userLoading } = useSupabaseUser();
  const { isAdmin, isLoading: adminLoading } = useIsAdmin();

  const enabled = process.env.NEXT_PUBLIC_AGENT_MODE_ENABLED === '1';
  const active = enabled && !!user && isAdmin;

  return {
    active,
    agentUserId: active ? user!.id : null,
    loading: userLoading || adminLoading,
  };
}
